import React, { memo, useCallback, useMemo, useState } from "react";
import { AdminTable, StatusBadge, IconButton } from "components/admin";
import { ColumnDef } from "types/admin";
import EditReferralModal from "./EditReferralModal";
import { ReferralCreditConfigAPI, ReferralCreditConfigRequest } from "apis/adminServices/referralServices";

export type { ReferralCreditConfigAPI };

interface ReferralTableProps {
  configurations: ReferralCreditConfigAPI[];
  loading: boolean;
  onUpdate: (id: number, payload: ReferralCreditConfigRequest) => Promise<void>;
}

const ReferralTable: React.FC<ReferralTableProps> = memo(
  ({ configurations, loading, onUpdate }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState<ReferralCreditConfigAPI | null>(null);

    const openModal = useCallback((config: ReferralCreditConfigAPI) => {
      setSelectedConfig(config);
      setIsModalOpen(true);
    }, []);

    const closeModal = useCallback(() => {
      setIsModalOpen(false);
      setSelectedConfig(null);
    }, []);

    const handleUpdate = useCallback(
      async (id: number, payload: ReferralCreditConfigRequest) => {
        try {
          await onUpdate(id, payload);
          closeModal();
        } catch (err) {
          console.error("Update failed:", err);
        }
      },
      [onUpdate, closeModal]
    );

    const columns = useMemo<ColumnDef<ReferralCreditConfigAPI>[]>(
      () => [
        { key: "configName", header: "Configuration Name" },
        {
          key: "referrerCreditAmount",
          header: "Referrer Credit",
          render: (c) => c.referrerCreditAmount,
        },
        {
          key: "refereeCreditAmount",
          header: "Referee Credit",
          render: (c) => c.refereeCreditAmount,
        },
        {
          key: "nonCommunityReferrerCredit",
          header: "Non-Community Referrer Credit",
          render: (c) => c.nonCommunityReferrerCredit,
        },
        {
          key: "nonCommunityRefereeCredit",
          header: "Non-Community Referee Credit",
          render: (c) => c.nonCommunityRefereeCredit,
        },
        {
          key: "communityCount",
          header: "Community Count",
          render: (c) => c.communityCount,
        },
        {
          key: "validTill",
          header: "Valid Till",
          render: (c) => (c.validTill ? new Date(c.validTill).toLocaleDateString("en-GB") : "-"),
        },
        {
          key: "isActive",
          header: "Status",
          render: (c) => <StatusBadge status={c.isActive} />,
        },
      ],
      []
    );

    const renderActions = useCallback(
      (config: ReferralCreditConfigAPI) => (
        <IconButton
          variant="edit"
          onClick={() => openModal(config)}
          title="Edit"
          ariaLabel="Edit referral configuration"
        />
      ),
      [openModal]
    );

    return (
      <>
        <AdminTable
          columns={columns}
          data={configurations}
          loading={loading}
          emptyMessage="No referral configurations found"
          renderActions={renderActions}
          showSerialNumber={true}
        />

        {selectedConfig && (
          <EditReferralModal
            isOpen={isModalOpen}
            config={selectedConfig}
            onClose={closeModal}
            onUpdate={handleUpdate}
          />
        )}
      </>
    );
  }
);

ReferralTable.displayName = "ReferralTable";

export default ReferralTable;
